import jsPDF from "jspdf";
import "jspdf-autotable";
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables, ChartDataLabels);

const formatDecimal = (value) => {
    const number = parseFloat(String(value).replace(',', '.'));
    if (isNaN(number)) {
        return '0,00';
    }
    return number.toFixed(2).replace('.', ',');
};

const getLogoByAttribute = (empresaLogoAtributo, logoNormal, logo4S) => {
    const valorNormalizado = (empresaLogoAtributo || "").trim().toLowerCase();
    if (valorNormalizado === "4s reflorestamento" || valorNormalizado === "4s") {
        return logo4S || logoNormal;
    }
    return logoNormal;
};

// Função auxiliar para carregar a imagem com segurança
const safeAddImage = (pdf, imageSource, format, x, y, width, height) => {
    try {
        if (imageSource) {
            pdf.addImage(imageSource, format, x, y, width, height);
        }
    } catch (error) {
        console.warn("Aviso: Falha ao renderizar logo no PDF, gerando sem imagem:", error);
    }
};

export const gerarOrdemPDF = ({ selectedForOrder, despesas, imovel, formatCurrency, formatDate, logo, logo4S, numeroOrdem }) => {
    if (!selectedForOrder || selectedForOrder.length === 0) {
        alert("Por favor, selecione ao menos uma despesa para gerar a ordem!");
        return;
    }

    const primeiraDespesa = despesas.find((d) => d.id === selectedForOrder[0]) || {};
    const logoParaUsar = getLogoByAttribute(primeiraDespesa.empresa_logo, logo, logo4S);
    
    const fornecedorDaDespesa = primeiraDespesa.fornecedor || "N/A";
    const codigoCcDaDespesa = primeiraDespesa.codigo_cc || "N/A";
    const numeroOrdemFinal = numeroOrdem || 0;
    
    const pdf = new jsPDF();
    const contentWidth = 180;
    const margin = (pdf.internal.pageSize.width - contentWidth) / 2;
    const startY = 40;
    const primaryColor = [22, 160, 133];
    const textPrimaryColor = [30, 30, 30];
    const textSecondaryColor = [120, 120, 120];
    const imgWidth = 45;
    const imgHeight = 22.5;

    safeAddImage(pdf, logoParaUsar, "PNG", (pdf.internal.pageSize.width - imgWidth) / 2, 10, imgWidth, imgHeight);
    
    let currentY = startY + 10;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(...textPrimaryColor);
    pdf.text(`Ordem de Pagamento #${numeroOrdemFinal}`, margin, currentY);
    
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...textSecondaryColor);
    pdf.text(`Despesa CC: ${codigoCcDaDespesa}`, margin + contentWidth, currentY - 2, { align: 'right' });
    pdf.text(`Data: ${new Date().toLocaleDateString("pt-BR", { timeZone: "UTC" })}`, margin + contentWidth, currentY + 3, { align: 'right' });
    
    currentY += 12;
    const drawInfoLine = (label, value, y) => {
        pdf.setFontSize(10);
        pdf.setTextColor(...textSecondaryColor);
        pdf.setFont("helvetica", "normal");
        pdf.text(label, margin, y);
        pdf.setTextColor(...textPrimaryColor);
        pdf.setFont("helvetica", "bold");
        pdf.text(value, margin + 35, y);
    };

    drawInfoLine("PROPRIETÁRIO:", imovel?.proprietario || "N/A", currentY);
    currentY += 7;
    drawInfoLine("FORNECEDOR:", fornecedorDaDespesa, currentY);
    currentY += 7;
    drawInfoLine("IMÓVEL/FAZENDA:", imovel?.descricao || "N/A", currentY);

    const tableColumns = ["Descrição", "Tipo de Despesa", "Quantidade", "Valor Unitário", "Vencimento", "Total"];
    const tableRows = selectedForOrder.map((despesaId) => {
        const despesa = despesas.find((d) => d.id === despesaId);
        if (!despesa) return ["Item N/A", "", "0,00", "R$ 0,00", "N/A", "R$ 0,00"];
        return [
            despesa.descricao || "",
            despesa.tipo_de_despesa || "",
            formatDecimal(despesa.quantidade),
            formatCurrency(despesa.valor_unitario),
            formatDate(despesa.validade),
            formatCurrency(despesa.total),
        ];
    });

    pdf.autoTable({
        startY: currentY + 8,
        head: [tableColumns],
        body: tableRows,
        theme: "grid",
        headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', fontSize: 10 },
        styles: { lineWidth: 0.1, lineColor: [200, 200, 200], font: 'helvetica', fontSize: 9, textColor: textPrimaryColor, cellPadding: 2.5 },
        columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 5: { halign: 'right' } },
        tableWidth: contentWidth,
        margin: { left: margin },
    });

    const totalGeral = selectedForOrder.reduce((total, despesaId) => {
        const despesa = despesas.find((d) => d.id === despesaId);
        return total + (despesa ? (parseFloat(despesa.total) || 0) : 0);
    }, 0);

    const totalY = pdf.lastAutoTable.finalY + 8;
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...textSecondaryColor);
    pdf.text('TOTAL GERAL', margin + contentWidth - 45, totalY, { align: 'right' });
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...textPrimaryColor);
    pdf.text(formatCurrency(totalGeral), margin + contentWidth, totalY, { align: 'right' });

    const finalY = totalY + 8;
    const rectHeight = finalY - startY;
    pdf.setDrawColor(...textSecondaryColor);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(margin - 7, startY - 7, contentWidth + 14, rectHeight, 4, 4);
    
    pdf.save(`ordem-de-pagamento-${numeroOrdemFinal}.pdf`);
};

export const gerarRelatorioPDF = async ({ reportOptions, despesas, imovel, formatCurrency, formatDate, logo, logo4S, chartContainerRef }) => {
    if (!reportOptions.startDate || !reportOptions.endDate) {
        alert("Você deve selecionar um intervalo de datas.");
        return;
    }

    const startDate = new Date(reportOptions.startDate);
    const endDate = new Date(reportOptions.endDate);
    endDate.setUTCHours(23, 59, 59, 999);

    const filteredDespesas = despesas.filter((d) => {
        const expenseDate = new Date(d.data || d.validade);
        if (isNaN(expenseDate.getTime())) return false;
        return expenseDate >= startDate && expenseDate <= endDate;
    });

    if (filteredDespesas.length === 0) {
        alert("Nenhuma despesa encontrada para o período e itens selecionados.");
        return;
    }

    const logoParaUsar = getLogoByAttribute(filteredDespesas[0]?.empresa_logo, logo, logo4S);

    const pdf = new jsPDF("landscape");
    const contentWidth = 277;
    const marginLeft = 10;
    const pageHeight = pdf.internal.pageSize.height;
    
    const headerColor = [39, 174, 96];
    const textColor = [45, 45, 45];
    const lightGrayColor = [240, 240, 240];
    const chartColors = ['#2ecc71', '#27ae60', '#1abc9c', '#16a085', '#00d2d3', '#3498db', '#9b59b6'];

    const addChartToPdf = async (chartId, chartConfig, x, y, width, height) => {
        const container = chartContainerRef.current;
        if (!container) return;
        const canvas = document.createElement('canvas');
        canvas.id = chartId;
        canvas.width = width * 3;
        canvas.height = height * 3;
        container.appendChild(canvas);
        try {
            const backgroundColorPlugin = {
                id: 'customBackgroundColor',
                beforeDraw: (chart) => {
                    const { ctx } = chart;
                    ctx.save();
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, chart.width, chart.height);
                    ctx.restore();
                }
            };
            chartConfig.plugins = [...(chartConfig.plugins || []), backgroundColorPlugin, ChartDataLabels];
            const chart = new Chart(canvas.getContext('2d'), chartConfig);
            await new Promise(resolve => setTimeout(resolve, 500));
            const imgData = chart.toBase64Image('image/png', 1.0);
            pdf.addImage(imgData, 'PNG', x, y, width, height, undefined, 'FAST');
            chart.destroy();
        } catch (e) { 
            console.error(`Erro ao gerar gráfico ${chartId}:`, e); 
        } finally { 
            if (container.contains(canvas)) container.removeChild(canvas); 
        }
    };
    
    const drawSectionHeader = (text, y) => {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(14);
        pdf.setTextColor(255, 255, 255);
        pdf.setFillColor(...headerColor);
        pdf.rect(marginLeft, y - 6, contentWidth, 10, 'F');
        pdf.text(text, marginLeft + 3, y);
    };

    safeAddImage(pdf, logoParaUsar, "PNG", marginLeft, 8, 35, 14);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(...headerColor);
    pdf.text(`Relatório de Despesas - ${imovel?.descricao || "Imóvel"}`, contentWidth + marginLeft, 15, { align: "right" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(...textColor);
    pdf.text(`Período de ${formatDate(reportOptions.startDate)} a ${formatDate(reportOptions.endDate)}`, contentWidth + marginLeft, 21, { align: "right" });
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(...headerColor);
    pdf.line(marginLeft, 28, contentWidth + marginLeft, 28);
    
    let currentY = 35;
    
    drawSectionHeader("Lançamentos Gerais no Período", currentY);
    currentY += 12;

    const tableColumns = [];
    if (reportOptions.columns.tipoDeDespesa) tableColumns.push("Tipo");
    if (reportOptions.columns.fornecedor) tableColumns.push("Fornecedor");
    if (reportOptions.columns.produto) tableColumns.push("Produto");
    if (reportOptions.columns.unidade) tableColumns.push("Un.");
    if (reportOptions.columns.quantidade) tableColumns.push("Qtd.");
    if (reportOptions.columns.valorUnitario) tableColumns.push("V. Unit.");
    if (reportOptions.columns.total) tableColumns.push("Total");
    if (reportOptions.columns.vencimento) tableColumns.push("Vencimento");

    const tableRows = filteredDespesas.map((despesa) => {
        const row = [];
        if (reportOptions.columns.tipoDeDespesa) row.push(despesa.tipo_de_despesa || "");
        if (reportOptions.columns.fornecedor) row.push(despesa.fornecedor || "");
        if (reportOptions.columns.produto) row.push(despesa.produto || "");
        if (reportOptions.columns.unidade) row.push(despesa.unidade || "");
        if (reportOptions.columns.quantidade) row.push(formatDecimal(despesa.quantidade));
        if (reportOptions.columns.valorUnitario) row.push(formatCurrency(despesa.valor_unitario));
        if (reportOptions.columns.total) row.push(formatCurrency(despesa.total));
        if (reportOptions.columns.vencimento) row.push(formatDate(despesa.validade));
        return row;
    });

    pdf.autoTable({
        startY: currentY,
        head: [tableColumns],
        body: tableRows,
        headStyles: { fillColor: headerColor, fontSize: 8 },
        styles: { fontSize: 7, cellPadding: 1.5, textColor: textColor },
        theme: "grid",
    });

    currentY = pdf.lastAutoTable.finalY + 10;
    
    const totalGeralPeriodo = filteredDespesas.reduce((sum, d) => sum + (parseFloat(d.total) || 0), 0);
    const totalQuantidadePeriodo = filteredDespesas.reduce((sum, d) => sum + (parseFloat(String(d.quantidade).replace(',', '.')) || 0), 0);
    
    const totalBlockHeight = 18; 
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "bold");
    pdf.setFillColor(...headerColor);
    pdf.setDrawColor(...headerColor);
    pdf.roundedRect(marginLeft, currentY, contentWidth, totalBlockHeight, 2, 2, 'FD');
    pdf.setTextColor(255, 255, 255);
    
    pdf.text("TOTAL GERAL NO PERÍODO:", marginLeft + 5, currentY + 6.5);
    pdf.text(formatCurrency(totalGeralPeriodo), marginLeft + contentWidth - 5, currentY + 6.5, { align: 'right' });
    
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text("SOMA DAS QUANTIDADES NO PERÍODO:", marginLeft + 5, currentY + 13.5);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text(formatDecimal(totalQuantidadePeriodo), marginLeft + contentWidth - 5, currentY + 13.5, { align: 'right' });

    currentY += totalBlockHeight + 7;

    const totalsByTypeForPDF = filteredDespesas.reduce((acc, despesa) => {
        const type = despesa.tipo_de_despesa || 'Não categorizado';
        if (!acc[type]) acc[type] = 0;
        acc[type] += parseFloat(despesa.total) || 0;
        return acc;
    }, {});
    
    if (Object.keys(totalsByTypeForPDF).length > 0) {
        if (currentY + 80 > pageHeight - 15) {
            pdf.addPage();
            currentY = 20;
        }

        drawSectionHeader("Análise Comparativa por Tipo de Despesa", currentY);
        currentY += 12;

        const summaryTableX = marginLeft;
        const summaryTableWidth = contentWidth * 0.4;
        const chartX = summaryTableX + summaryTableWidth + 10;
        const chartWidth = contentWidth - summaryTableWidth - 10;
        const sectionStartY = currentY;

        const summaryTableBody = Object.entries(totalsByTypeForPDF).map(([type, total]) => {
            const percentage = totalGeralPeriodo > 0 ? (total / totalGeralPeriodo) * 100 : 0;
            return [type, formatCurrency(total), `${percentage.toFixed(2).replace('.', ',')} %`];
        });

        pdf.autoTable({
            startY: sectionStartY,
            head: [['Tipo de Despesa', 'Valor Total', 'Percentual (%)']],
            body: summaryTableBody,
            theme: 'striped',
            headStyles: { fillColor: headerColor, fontSize: 9 },
            styles: { fontSize: 8, cellPadding: 1.5, textColor: textColor },
            margin: { left: summaryTableX },
            tableWidth: summaryTableWidth,
            columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
        });
        
        const chartHeight = Math.max(70, summaryTableBody.length * 9);
        const comparativeChartConfigPDF = {
            type: 'bar',
            data: {
                labels: Object.keys(totalsByTypeForPDF),
                datasets: [{
                    data: Object.values(totalsByTypeForPDF),
                    backgroundColor: chartColors,
                    borderColor: headerColor,
                    borderWidth: 1,
                }]
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (c) => formatCurrency(c.raw) } },
                    datalabels: {
                        display: true, anchor: 'end', align: 'right',
                        formatter: (value) => formatCurrency(value),
                        color: textColor, font: { size: 9, weight: 'bold' }
                    }
                },
                scales: {
                    x: { ticks: { callback: (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(v) } },
                    y: { ticks: { font: { size: 8 } } }
                }
            }
        };
        await addChartToPdf('comparative-chart-pdf', comparativeChartConfigPDF, chartX, sectionStartY, chartWidth, chartHeight);
    }
    
    pdf.save(`relatorio-despesas-${imovel?.descricao?.replace(/\s+/g, '-') || 'imovel'}.pdf`);
};